import { useMemo, useState } from "react"
import { AlertTriangle } from "lucide-react"
import { useTranslation } from "react-i18next"

import { LoadingState } from "@/components/loading-state"
import { Card } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { useLotsForOT } from "@/features/transit/hooks/use-lots-for-ot"
import { lotQCSummary, lotQCWarning } from "@/features/transit/lib/qc-summary"
import type { LotListItemWithQC } from "@/features/lots/api/types"
import type { Contract } from "@/types/domain"

interface StepLotsProps {
  contract: Contract | null
  selected: Set<number>
  onToggle: (lotId: number) => void
}

export function StepLots({ contract, selected, onToggle }: StepLotsProps) {
  const { t } = useTranslation(["transit", "contracts"])
  // Backend filters with `available=true` (set in `useLotsForOT`), so the
  // returned list already excludes lots attached to other in-flight OTs.
  const { data: lots, isLoading: lotsLoading } = useLotsForOT()
  const allLots = useMemo(() => lots ?? [], [lots])

  // CADESA OT workshop: by default the wizard only shows lots that have
  // been accepted by the client for this exact contract (a CLIENT-scoped
  // ACCEPTED log on this contractId). Operators can flip the toggle to
  // inspect the full list, e.g. to see why a particular lot is hidden.
  const [showAll, setShowAll] = useState(false)
  const contractId = contract ? Number(contract.id) : null
  const candidates = useMemo(() => {
    if (showAll) return allLots
    return allLots.filter((l) => {
      const s = lotQCSummary(l, { contractId })
      return s.contractAccepted
    })
  }, [allLots, showAll, contractId])
  const hiddenCount = allLots.length - candidates.length

  const stats = useMemo(() => {
    const list = candidates.filter((l) => selected.has(l.id))
    const totalKg = list.reduce((acc, l) => acc + (l.netKg ?? 0), 0)
    const totalBags = list.reduce((acc, l) => acc + (l.bags ?? 0), 0)
    return {
      count: list.length,
      totalKg,
      totalMT: totalKg / 1000,
      totalBags,
    }
  }, [candidates, selected])

  if (lotsLoading) {
    return <LoadingState />
  }

  const cert = contract?.certificates?.[0]
  const certLabel = cert
    ? t(`options.certificates.${cert}`, { ns: "contracts", defaultValue: cert })
    : null
  const labelPreview = contract
    ? `${contract.clientName ?? "—"}${certLabel ? ` — ${certLabel}` : ""}`
    : "—"

  return (
    <div className="flex flex-col gap-4">
      <p className="text-muted-foreground text-sm">
        {t("wizard.step2.intro", {
          client: contract?.clientName ?? "—",
        })}
      </p>

      <Card className="bg-muted/40 flex flex-col gap-1 p-3">
        <div className="text-muted-foreground text-[10px] font-semibold uppercase tracking-wide">
          {t("wizard.step3.labelPreview")}
        </div>
        <div className="text-foreground text-sm font-medium">{labelPreview}</div>
        <div className="text-muted-foreground text-[11px]">
          {t("wizard.step3.labelHint")}
        </div>
      </Card>

      <div className="grid grid-cols-3 gap-3">
        <StatCard
          label={t("wizard.step2.selectedLots")}
          value={stats.count.toString()}
        />
        <StatCard
          label={t("wizard.step2.selectedBags")}
          value={stats.totalBags.toLocaleString()}
        />
        <StatCard
          label={t("wizard.step2.selectedMT")}
          value={`${stats.totalMT.toFixed(3)} MT`}
        />
      </div>

      <div className="flex items-center justify-between gap-3 rounded-md border bg-muted/30 px-3 py-2">
        <div className="text-xs">
          <Label className="text-xs">{t("wizard.step2.filterTitle")}</Label>
          <p className="text-muted-foreground mt-0.5">
            {showAll
              ? t("wizard.step2.filterShowingAll")
              : hiddenCount > 0
                ? t("wizard.step2.filterHidden", { count: hiddenCount })
                : t("wizard.step2.filterAcceptedOnly")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            id="show-all-lots"
            checked={showAll}
            onCheckedChange={(v) => setShowAll(!!v)}
          />
          <Label htmlFor="show-all-lots" className="text-xs">
            {t("wizard.step2.filterToggle")}
          </Label>
        </div>
      </div>

      {candidates.length === 0 ? (
        <Card className="border-dashed p-6 text-center text-sm text-muted-foreground">
          {allLots.length === 0
            ? t("wizard.step2.noLotsAvailable")
            : t("wizard.step2.filterAllHidden")}
        </Card>
      ) : (
        <Card className="max-h-80 overflow-y-auto p-0">
          <ul className="divide-y">
            {candidates.map((lot) => (
              <LotRow
                key={lot.id}
                lot={lot}
                contractId={contract ? Number(contract.id) : null}
                selected={selected.has(lot.id)}
                onToggle={() => onToggle(lot.id)}
              />
            ))}
          </ul>
        </Card>
      )}
    </div>
  )
}

interface LotRowProps {
  lot: LotListItemWithQC
  contractId: number | null
  selected: boolean
  onToggle: () => void
}

function LotRow({ lot, contractId, selected, onToggle }: LotRowProps) {
  const { t } = useTranslation("transit")
  const warning = lotQCWarning(lot, contractId)
  const netMT = (lot.netKg ?? 0) / 1000

  return (
    <li>
      <button
        type="button"
        onClick={onToggle}
        className={`flex w-full items-center gap-3 px-4 py-3 text-left transition-colors ${
          selected ? "bg-primary/5" : "hover:bg-muted/40"
        }`}
      >
        <Checkbox
          checked={selected}
          aria-label={t("wizard.step2.toggleLot", { lot: lot.lotNumber })}
          className="pointer-events-none size-5 border-2"
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm font-medium">
              {lot.lotNumber}
            </span>
            {lot.usinage?.usinageNumber ? (
              <span className="text-muted-foreground bg-muted rounded-sm px-1.5 py-0.5 text-[10px] font-mono">
                {t("wizard.step2.fromUsinage", {
                  usinage: lot.usinage.usinageNumber,
                })}
              </span>
            ) : null}
          </div>
          <div className="text-muted-foreground mt-0.5 text-xs tabular-nums">
            {t("wizard.step2.lotLine", {
              bags: lot.bags,
              mt: netMT.toFixed(3),
            })}
          </div>
        </div>

        {warning ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300 inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[10px] uppercase tracking-wide">
                <AlertTriangle className="size-3" />
                {t(`qcWarnings.${warning}`)}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {t(`qcWarnings.${warning}`)}
            </TooltipContent>
          </Tooltip>
        ) : null}
      </button>
    </li>
  )
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <Card className="flex flex-col gap-1 p-3">
      <div className="text-muted-foreground flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide">
        {label}
      </div>
      <div className="text-foreground text-2xl font-semibold tabular-nums leading-none">
        {value}
      </div>
    </Card>
  )
}

